import React, { useState, useEffect, useRef } from 'react';
import { useUser } from 'util/UserContext';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from 'config/axiosConfig';
import { Comment } from 'util/constants';
import CommentInput from 'components/Comments/CommentInput';
import RenderComments from 'components/Comments/RenderComments';
import AddButton from 'ui/AddButton';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import { AxiosError } from 'axios';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import ServerErrorPage from 'components/ResponsePages/500ServerErrorPage';
import ScrollHideContainer from 'ui/ScrollHideContainer';
import { PrivacyLevel } from 'util/userUtils';
import { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';

interface Props {
  commentable_type: Comment['commentable_type'];
  commentable_id: Comment['commentable_id'];
  isEntitySection: boolean;
}

const CommentsSection: React.FC<Props> = ({ commentable_type, commentable_id, isEntitySection }) => {
  const { currentUser, setShowAuthModal } = useUser();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const commentInputRef = useRef<HTMLDivElement>(null);
  const commentSectionRef = useRef<HTMLDivElement>(null);
  const { commentId } = useParams<{ commentId: string }>();
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchComments = async () => {
      try {
        let response;
        if (commentId) {
          response = await api.get(`/api/comments/${commentId}/replies`);
        } else {
          response = await api.get(`/api/comments/${commentable_type}/${commentable_id}`);
        }
        const allComments = response.data;

        // Filter out only root comments that are not active
        const filteredComments = allComments.filter((comment: Comment) => {
          if (comment.parent_id === null) {
            return comment.is_active !== false;
          }
          return true;
        });

        setComments(filteredComments);
        setLoading(false);
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          setError('404: Failed to load comments. Please try again later.');
        } else {
          setError('Failed to load comments. Please try again later.');
        }
        setLoading(false);
      }
    };

    fetchComments();
  }, [commentable_type, commentable_id, commentId]);

  const updateCommentsRecursively = (commentsArray: Comment[], updateFunction: (comment: Comment) => Comment): Comment[] => {
    return commentsArray.map(comment => {
      const updatedComment = updateFunction(comment);
      if (updatedComment.replies && updatedComment.replies.length > 0) {
        return {
          ...updatedComment,
          replies: updateCommentsRecursively(updatedComment.replies, updateFunction)
        };
      }
      return updatedComment;
    });
  };

  const addNewComment = async (content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => {
    try {
      const response = await api.post(`/api/comments/${commentable_type}/${commentable_id}`, {
        content,
        properties: {
          privacy: privacy
        }
      });
      let newComment = response.data;
      await api.post(`/api/votes/comment/${newComment.id}`, {
        user_id: currentUser?.id,
        rel_type: 'user_upvote',
      });

      // Add coauthors if any
      if (coauthors.length > 0) {
          try {
              await api.post(`/api/coauthors/comment/${newComment.id}/request`, {
                  addedCoauthors: coauthors.map(c => ({
                      id: c.id,
                      type: c.type
                  }))
              });
          } catch (err) {
              console.error('Error adding coauthors:', err);
              // Continue even if coauthors couldn't be added
          }
      }

      const updatedCommentResponse = await api.get(`/api/comments/${newComment.id}`);
      newComment = {
        ...updatedCommentResponse.data,
        replies: [], // Initialize replies as an empty array
      };
      setComments([newComment, ...comments]);
      toast.success('Comment added successfully');
    } catch (error) {
      console.error('Error adding new comment:', error);
      toast.error('Failed to add comment');
    }
  };

  const addReply = async (parent_id: number, content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[]) => {
    try {
      const response = await api.post(`/api/comments/${commentable_type}/${commentable_id}`, {
        content,
        parent_id: parent_id,
        properties: {
          privacy: privacy
        }
      });
      let newReply = response.data;
      await api.post(`/api/votes/comment/${newReply.id}`, {
        user_id: currentUser?.id,
        rel_type: 'user_upvote',
      });

      // Add coauthors if any
      if (coauthors.length > 0) {
          try {
              await api.post(`/api/coauthors/comment/${newReply.id}/request`, {
                  addedCoauthors: coauthors.map(c => ({
                      id: c.id,
                      type: c.type
                  }))
              });
          } catch (err) {
              console.error('Error adding coauthors:', err);
              // Continue even if coauthors couldn't be added
          }
      }

      const updatedReplyResponse = await api.get(`/api/comments/${newReply.id}`);
      newReply = {
        ...updatedReplyResponse.data,
        replies: [], // Initialize replies as an empty array
      };

      setComments(prevComments => updateCommentsRecursively(prevComments, comment => {
        if (comment.id === parent_id) {
          return {
            ...comment,
            replies: [...(comment.replies || []), newReply]
          };
        }
        return comment;
      }));
      toast.success('Reply added successfully');
    } catch (error) {
      console.error('Error adding reply:', error);
      toast.error('Failed to add reply');
    }
  };

  const updateComment = async (commentId: number, updatedContent: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => {
    try {//TODO: Change to patch
      const response = await api.put(`/api/comments/${commentId}`, {
        content: updatedContent,
        privacy: privacy
      });

      const updatedComment = response.data;

      if (addedCoauthors.length > 0 || removedCoauthors.length > 0) {
        try {
          await api.post(`/api/coauthors/comment/${commentId}/request`, {
            addedCoauthors: addedCoauthors.map((c: any) => ({
              id: c.id,
              type: c.type
            })),
            removedCoauthors: removedCoauthors.map((c: any) => ({
              id: c.id,
              type: c.type
            }))
          })
        } catch (err) {
          console.error('Error updating coauthors:', err)
        }
      }

      setComments(prevComments => updateCommentsRecursively(prevComments, comment => {
        if (comment.id === commentId) {
          return { ...comment, ...updatedComment };
        }
        return comment;
      }));
      toast.success('Comment updated successfully');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const deleteComment = async (commentId: number) => {
    try {
      await api.delete(`/api/comments/${commentId}`);

      setComments(prevComments => updateCommentsRecursively(prevComments, comment => {
        if (comment.id === commentId) {
          // Replace the comment content with a placeholder
          return { ...comment, content: '[Deleted by user]', username: '[deleted]' };
        }
        return comment;
      }));
      toast.success('Comment deleted successfully');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  /*useEffect(() => {
    if (showFullInput && commentInputRef.current) {
      commentInputRef.current.focus();
    }
  }, [showFullInput]);*/

  if (error) {
    return error.match(/404/) ? <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find the comments for this ${commentable_type}.`} errorSubtext={`Double check the URL or try reloading the page.`} buttonText={`View Other Comments`} buttonHref={`.`} /> : <ServerErrorPage />
  }

  if (commentId && comments.length < 1) {
    return <NotFoundPage displayStyle={`page`} errorMessage={`Sorry, we couldn't find this comment.`} errorSubtext={`It may be only visible to specific users. Double check the URL or try reloading the page.`} buttonText={`View Other Comments`} buttonHref={`.`} />
  }

  if (commentId) {
    return (
      <div className='comment-id-section mb-6'>
      <ScrollHideContainer 
        className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
        hideOffset={115}
        threshold={114}
      >
        <div className="flex justify-between items-center gap-2 mb-2 mx-2">
          {comments[0]?.root_id ? (
            <Link to={`../${comments[0]?.root_id}`} relative="path" preventScrollReset={true} className="internal-link text-sm font-semibold px-2">See full thread</Link>
          ) : (
            <span className="text-sm font-normal">This is the root comment</span>
          )}
          <span className="flex-1 h-px mx-xs bg-gray-300 dark:bg-eoe-dark-border-main" data-id="line"></span>
          <Link to=".." relative="path" preventScrollReset={true} className="internal-link text-sm font-semibold px-2">See all comments</Link>
        </div>
        <RenderComments
          comments={comments}
          maxDepth={30}
          isAllCollapsed={false}
          onAddReply={addReply}
          onUpdateComment={updateComment}
          onDeleteComment={deleteComment}
          commentable_type={commentable_type}
          commentable_id={commentable_id}
          isEntitySection={true}
        />
      </ScrollHideContainer>
      </div>
    );
  }
  
  return (
    <div className='comments-section pb-6'>
      <ScrollHideContainer 
        className="z-40 max-w-5xl bg-eoe-bg dark:bg-eoe-dark-bg border-b border-eoe-border-main dark:border-eoe-dark-border-main"
        hideOffset={115}
        threshold={114}
      >
      <div ref={commentSectionRef}>
        {!currentUser ? (
        
        <div className='flex mb-1'>
          <AddButton
              tooltipTitle="Add a comment"
              buttonText="Comment"
              onClick={() => setShowAuthModal(true)}
              className="mx-2 my-2 w-full"
          />
        </div>
        ) : ( 
          <div className="my-2 mx-4">
            <CommentInput
              action="add"
              onSubmit={addNewComment}
              buttonText="Comment"
              ref={commentInputRef}
            />
          </div>
        )}
      </div>
      </ScrollHideContainer>
        {loading && (
            <LoadingSkeleton 
                count={6}
                height='h-20'
                width=''
                margin='mx-4 pr-4'
                rounded='rounded-2xl'

            />
        )}
        {!loading &&comments.length === 0 ? (
          <p className="m-4 text-sm">Be the first to start a discussion!</p>
        ) : (
        <>
          <RenderComments
            comments={comments}
            maxDepth={30}
            isAllCollapsed={true}
            onAddReply={addReply}
            onUpdateComment={updateComment}
            onDeleteComment={deleteComment}
            commentable_type={commentable_type}
            commentable_id={commentable_id}
            isEntitySection={true}
          />
        </>
      )}
    </div>
  );
};

export default CommentsSection;